import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { defineMessages, useIntl } from "react-intl";
import SweetAlert from "sweetalert2";
import { useAuthorizedActions } from "../../components/authorization-verifier";
import { usePropertyConfiguration } from "../../components/property-configuration-provider";
import { SECTION } from "../../lib/constants";
import EntityList from "../witadmin/CRUD/EntityList/EntityList";
import CONSTANTS from "../witadmin/CRUD/constants";
import { B2BConditionEditor } from "./B2BConditionEditor";
import { buildConditionRows, ConditionRow } from "./conditionRows";
import {
  CommercialTermsRowChangeDto,
  CommercialTermsRowDto,
  deleteB2BCommercialTermsRow,
  listB2BCommercialTerms,
  listB2BEntities,
  updateB2BCommercialTermsRow,
} from "./infrastructure/B2BAdminApi";

const messages = defineMessages({
  title: { id: "b2bConditions.title", defaultMessage: "B2B commercial conditions" },
  loadError: {
    id: "b2bConditions.loadError",
    defaultMessage: "The commercial conditions could not be loaded.",
  },
  agency: { id: "b2bConditions.col.agency", defaultMessage: "Agency" },
  company: { id: "b2bConditions.col.company", defaultMessage: "Company" },
  commission: { id: "b2bConditions.col.commission", defaultMessage: "Commission" },
  validity: { id: "b2bConditions.col.validity", defaultMessage: "Validity" },
  properties: { id: "b2bConditions.col.properties", defaultMessage: "Properties" },
  view: { id: "b2bConditions.action.view", defaultMessage: "View" },
  salesforceOwned: {
    id: "b2bConditions.action.salesforceOwned",
    defaultMessage: "Published by Salesforce: turn on B2B master mode to change it here",
  },
  edit: { id: "b2bConditions.action.edit", defaultMessage: "Edit" },
  delete: { id: "b2bConditions.action.delete", defaultMessage: "Delete" },
  deleteConfirm: {
    id: "b2bConditions.action.deleteConfirm",
    defaultMessage:
      "Delete this commercial condition? The next Salesforce publication for this entity brings it back.",
  },
  deleteYes: { id: "b2bConditions.action.deleteYes", defaultMessage: "Delete" },
  deleteNo: { id: "b2bConditions.action.deleteNo", defaultMessage: "Cancel" },
  saveError: {
    id: "b2bConditions.action.saveError",
    defaultMessage: "The change could not be saved.",
  },
  masterModeOff: {
    id: "b2bConditions.action.masterModeOff",
    defaultMessage:
      "backoffice-manager does not accept edits here: its master mode is off for this environment.",
  },
});

const COLUMNS: (keyof Omit<ConditionRow, "id" | "source">)[] = [
  "agency",
  "company",
  "commission",
  "validity",
  "properties",
];

// The table reads validation per column, so it cannot be left out.
const NO_VALIDATION = {};

/** The standard backoffice listing: read-only, as Salesforce owns the data, unless the property is in master mode. */
export function B2BConditionsList() {
  const intl = useIntl();
  const masterMode = usePropertyConfiguration()?.["b2bMasterModeEnabled"] === "1";
  // Not this section's: its modify/delete would make EntityList add its own edit and delete buttons.
  const permissions = useAuthorizedActions(SECTION.LOYALTY_ORGANIZATIONS);
  const canEdit = masterMode && !!permissions?.modify;
  // backoffice-manager also requires the modify scope to delete a row.
  const canDelete = canEdit && !!permissions?.delete;
  // backoffice-manager has its own deployment-wide switch; say so instead of a generic failure.
  const failureOf = useCallback(
    (error: unknown) =>
      intl.formatMessage(
        String((error as { message?: string })?.message).includes("MASTER_MODE_OFF")
          ? messages.masterModeOff
          : messages.saveError
      ),
    [intl]
  );
  const [rows, setRows] = useState<ConditionRow[]>();
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState<CommercialTermsRowDto>();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [readOnly, setReadOnly] = useState(true);

  const load = useCallback(
    () =>
      Promise.all([listB2BCommercialTerms(), listB2BEntities()])
        .then(([terms, entities]) => setRows(buildConditionRows(terms, entities, intl)))
        .catch(error => {
          console.error("[b2bConditions] could not load the commercial conditions", error);
          setFailed(true);
        }),
    [intl]
  );

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = useCallback(
    async (change: CommercialTermsRowChangeDto) => {
      if (!editing) {
        return;
      }
      setSaving(true);
      setSaveError(undefined);
      try {
        await updateB2BCommercialTermsRow(editing, change);
        setEditing(undefined);
        await load();
      } catch (error) {
        console.error("[b2bConditions] could not save the commercial condition", error);
        setSaveError(failureOf(error));
      } finally {
        setSaving(false);
      }
    },
    [editing, failureOf, load]
  );

  const handleDelete = useCallback(
    async (row: CommercialTermsRowDto) => {
      const { isConfirmed } = await SweetAlert.fire({
        icon: "warning",
        text: intl.formatMessage(messages.deleteConfirm),
        showCancelButton: true,
        confirmButtonText: intl.formatMessage(messages.deleteYes),
        cancelButtonText: intl.formatMessage(messages.deleteNo),
      });
      if (!isConfirmed) {
        return;
      }
      try {
        await deleteB2BCommercialTermsRow(row);
        await load();
      } catch (error) {
        console.error("[b2bConditions] could not delete the commercial condition", error);
        await SweetAlert.fire({ icon: "error", text: failureOf(error) });
      }
    },
    [intl, failureOf, load]
  );

  // Without master mode the actions stay visible but disabled: the row can only be consulted.
  const customActions = useMemo(
    () => [
      {
        icon: "fa fa-eye",
        tooltipId: "b2b-condition-view",
        tooltipText: intl.formatMessage(messages.view),
        onExecute: ({ entity }: { entity: ConditionRow }) => {
          setReadOnly(true);
          setSaveError(undefined);
          setEditing(entity.source);
        },
      },
      {
        icon: "fa fa-pencil",
        tooltipId: "b2b-condition-edit",
        tooltipText: intl.formatMessage(canEdit ? messages.edit : messages.salesforceOwned),
        classNames: canEdit ? "" : "disabled",
        onExecute: ({ entity }: { entity: ConditionRow }) => {
          if (!canEdit) {
            return;
          }
          setReadOnly(false);
          setSaveError(undefined);
          setEditing(entity.source);
        },
      },
      {
        icon: "fa fa-trash",
        tooltipId: "b2b-condition-delete",
        tooltipText: intl.formatMessage(canDelete ? messages.delete : messages.salesforceOwned),
        classNames: canDelete ? "" : "disabled",
        onExecute: ({ entity }: { entity: ConditionRow }) =>
          canDelete && handleDelete(entity.source),
      },
    ],
    [canEdit, canDelete, intl, handleDelete]
  );

  const fields = useMemo(
    () => ({
      list: COLUMNS.map(column => ({
        fieldName: column,
        displayName: intl.formatMessage(messages[column]),
      })),
    }),
    [intl]
  );

  if (failed) {
    return <Alert variant="danger">{intl.formatMessage(messages.loadError)}</Alert>;
  }
  return (
    <>
      <EntityList
        loading={!rows}
        disableSorting
        name={intl.formatMessage(messages.title)}
        entityName={CONSTANTS.ENTITY.B2BCOMMERCIALCONDITION.name}
        entities={rows ?? []}
        fields={fields}
        validation={NO_VALIDATION}
        renderers={{ list: {} }}
        filterFields={["agency", "company", "validity"]}
        allowClone={false}
        reorder={false}
        customActions={customActions}
      />
      <B2BConditionEditor
        row={editing}
        readOnly={readOnly}
        saving={saving}
        error={saveError}
        onHide={() => setEditing(undefined)}
        onSave={handleSave}
      />
    </>
  );
}
